import React, { useMemo, useState } from 'react';
import {
  Button,
  Dropdown,
  NumberInput,
  Loading,
  Tag,
  TimePicker,
  TimePickerSelect,
  SelectItem,
  TextArea,
  Toggle,
  InlineNotification,
} from '@carbon/react';
import { Add, Calendar as CalendarIcon, TrashCan } from '@carbon/react/icons';

import { useGetGroupsQuery } from '@/redux/new_services/groups';
import { useGetFederalStatesQuery } from '@/redux/new_services/federalStates';
import {
  useGetGroupOpeningHoursQuery,
  useCreateOpeningHoursMutation,
  useUpdateOpeningHoursMutation,
  useDeleteOpeningHoursMutation,
  useUpdateGroupHolidayMutation,
} from '@/redux/new_services/groupHolidays';

import './YearOpeningHoursPage.scss';

// YearOpeningHoursPage — admin pflegt für eine Gruppe + ein Jahr die
// konkreten Öffnungszeiten je Feiertag. Pro Feiertag eine Card mit
// Standard-Zeile (gilt für alle dem Holiday zugeordneten Bundesländer)
// plus optionalen Bundesland-Abweichungen.
const YearOpeningHoursPage = () => {
  const { data: groupsResp } = useGetGroupsQuery();
  const groups = groupsResp?.data || [];
  const [groupId, setGroupId] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());

  const { data: items = [], isLoading } = useGetGroupOpeningHoursQuery(
    { groupId, year },
    { skip: !groupId },
  );

  return (
    <div className="oh-page">
      <header className="oh-page__head">
        <h2>Öffnungszeiten an Feiertagen</h2>
        <div className="oh-page__pickers">
          <Dropdown
            id="ohp-group-picker"
            titleText="Gruppe"
            label="Gruppe wählen"
            items={groups}
            itemToString={(g) => (g ? g.name : '')}
            onChange={({ selectedItem }) => setGroupId(selectedItem?.model?.ID || null)}
          />
          <NumberInput
            id="ohp-year"
            label="Jahr"
            min={2000}
            max={2100}
            value={year}
            onChange={(_e, { value }) => setYear(Number(value))}
          />
        </div>
      </header>

      {isLoading && <Loading active small withOverlay={false} />}

      {!groupId && (
        <InlineNotification
          kind="info"
          title="Hinweis"
          subtitle="Wähle eine Gruppe und ein Jahr, um die Feiertags-Öffnungszeiten zu pflegen."
          hideCloseButton
          lowContrast
        />
      )}

      {groupId && items.length === 0 && !isLoading && (
        <InlineNotification
          kind="warning"
          title="Keine Feiertage aktiviert"
          subtitle={'Für diese Gruppe sind aktuell keine Feiertage zugeordnet. Wechsle in den Bereich „Pro Gruppe", um welche zu aktivieren.'}
          hideCloseButton
          lowContrast
        />
      )}

      {groupId && items.map((item) => (
        <HolidayCard
          key={item.group_holiday.model.ID}
          groupId={groupId}
          year={year}
          item={item}
        />
      ))}
    </div>
  );
};

// HolidayCard rendert einen Feiertag mit Datum, lead-days-Konfiguration,
// einer Standard-Öffnungszeit-Zeile und je einer Zeile pro Bundesland-
// Abweichung.
const HolidayCard = ({ groupId, year, item }) => {
  const { data: federalStates = [] } = useGetFederalStatesQuery();
  const [createOH] = useCreateOpeningHoursMutation();
  const [updateGH] = useUpdateGroupHolidayMutation();

  const groupHoliday = item.group_holiday;
  const holiday = groupHoliday.holiday;
  const date = item.date ? new Date(item.date) : null;

  // Default-Zeile (federal_state_id == null) und alle Abweichungen
  const defaultRow = (item.opening_hours || []).find((oh) => !oh.federal_state_id);
  const deviations = (item.opening_hours || []).filter((oh) => oh.federal_state_id);

  // Bundesländer ohne Abweichung — Admin kann eine ergänzen
  const eligibleStates = useMemo(() => {
    const used = new Set(deviations.map((d) => d.federal_state_id));
    return (holiday?.federal_states || []).filter((s) => !used.has(s.id));
  }, [holiday, deviations]);

  const ensureDefault = async () => {
    if (defaultRow) return;
    try {
      await createOH({
        groupId,
        year,
        group_id: groupId,
        holiday_id: holiday.model.ID,
        is_closed: true,
      }).unwrap();
    } catch (e) {
      console.warn('create default opening hours:', e);
    }
  };

  const addDeviation = async (stateId) => {
    try {
      await createOH({
        groupId,
        year,
        group_id: groupId,
        holiday_id: holiday.model.ID,
        federal_state_id: stateId,
        is_closed: defaultRow?.is_closed ?? true,
        open_from: defaultRow?.open_from || '',
        open_until: defaultRow?.open_until || '',
      }).unwrap();
    } catch (e) {
      console.warn('add deviation:', e);
    }
  };

  const setLeadDays = (n) => {
    updateGH({
      id: groupHoliday.model.ID,
      groupId,
      variant_id: groupHoliday.variant_id,
      lead_days: Math.max(0, Number(n) || 0),
    });
  };

  return (
    <article className="oh-card">
      <header className="oh-card__head">
        <div className="oh-card__title">
          <h3>{holiday?.name}</h3>
          {date && (
            <span className="oh-card__date">
              <CalendarIcon size={16} /> {date.toLocaleDateString('de-DE', {
                weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric',
              })}
            </span>
          )}
        </div>
        <div className="oh-card__states">
          {(holiday?.federal_states || []).map((s) => (
            <Tag key={s.code} type="cool-gray" size="sm">{s.code}</Tag>
          ))}
        </div>
      </header>

      <div className="oh-card__lead">
        <NumberInput
          id={`lead-${groupHoliday.model.ID}`}
          label="Auf Geräten anzeigen ab (Tage vorher)"
          helperText="0 = nur am Feiertag selbst"
          min={0}
          max={60}
          value={groupHoliday.lead_days || 0}
          onChange={(_e, { value }) => setLeadDays(value)}
        />
      </div>

      <section className="oh-card__rows">
        {!defaultRow ? (
          <Button kind="tertiary" renderIcon={Add} onClick={ensureDefault}>
            Standard-Öffnungszeit anlegen
          </Button>
        ) : (
          <OpeningHoursRow
            row={defaultRow}
            groupId={groupId}
            year={year}
            federalStates={federalStates}
            isDefault
          />
        )}

        {deviations.map((d) => (
          <OpeningHoursRow
            key={d.model.ID}
            row={d}
            groupId={groupId}
            year={year}
            federalStates={federalStates}
          />
        ))}
      </section>

      {defaultRow && eligibleStates.length > 0 && (
        <div className="oh-card__add-deviation">
          <Dropdown
            id={`dev-pick-${groupHoliday.model.ID}`}
            titleText="Abweichende Öffnungszeit für Bundesland"
            label="Bundesland wählen"
            items={eligibleStates}
            itemToString={(s) => (s ? s.name : '')}
            onChange={({ selectedItem }) => selectedItem && addDeviation(selectedItem.id)}
          />
        </div>
      )}
    </article>
  );
};

// Eine Öffnungszeit-Zeile: entweder Standard (gilt für alle Bundesländer)
// oder eine Bundesland-spezifische Abweichung.
const OpeningHoursRow = ({ row, groupId, year, federalStates, isDefault }) => {
  const [updateOH] = useUpdateOpeningHoursMutation();
  const [deleteOH] = useDeleteOpeningHoursMutation();

  // Lokaler Draft, damit TimePicker während Tipperei nicht jeden Keystroke
  // ans Backend schickt. Beim Blur / Toggle wird persistiert.
  const [draft, setDraft] = useState({
    is_closed: row.is_closed,
    open_from: row.open_from || '',
    open_until: row.open_until || '',
    notes: row.notes || '',
  });

  const stateName = row.federal_state_id
    ? federalStates.find((s) => s.id === row.federal_state_id)?.name || '—'
    : 'Standard (alle zugeordneten Bundesländer)';

  const persist = (partial) => {
    const next = { ...draft, ...partial };
    setDraft(next);
    updateOH({
      id: row.model.ID,
      groupId,
      year,
      group_id: row.group_id,
      holiday_id: row.holiday_id,
      federal_state_id: row.federal_state_id,
      ...next,
    });
  };

  return (
    <div className={`oh-row ${isDefault ? 'oh-row--default' : 'oh-row--deviation'}`}>
      <div className="oh-row__head">
        <div className="oh-row__label">
          {isDefault && <Tag type="green" size="sm">Standard</Tag>}
          {!isDefault && <Tag type="purple" size="sm">Abweichung</Tag>}
          <span>{stateName}</span>
        </div>
        <Toggle
          id={`closed-${row.model.ID}`}
          labelText=""
          labelA="Geschlossen"
          labelB="Geöffnet"
          toggled={!draft.is_closed}
          onToggle={(open) => persist({ is_closed: !open })}
          size="sm"
        />
        {!isDefault && (
          <Button
            kind="ghost"
            size="sm"
            renderIcon={TrashCan}
            hasIconOnly
            iconDescription="Bundesland-Abweichung entfernen"
            onClick={() => deleteOH({ id: row.model.ID, groupId, year })}
          />
        )}
      </div>

      {!draft.is_closed && (
        <div className="oh-row__times">
          <TimePicker
            id={`from-${row.model.ID}`}
            labelText="Öffnet"
            value={draft.open_from}
            placeholder="08:00"
            maxLength={5}
            onChange={(e) => setDraft({ ...draft, open_from: e.target.value })}
            onBlur={() => persist({})}
          >
            <TimePickerSelect
              id={`from-period-${row.model.ID}`}
              defaultValue="24h"
              disabled
            >
              <SelectItem value="24h" text="" />
            </TimePickerSelect>
          </TimePicker>
          <TimePicker
            id={`until-${row.model.ID}`}
            labelText="Schließt"
            value={draft.open_until}
            placeholder="14:00"
            maxLength={5}
            onChange={(e) => setDraft({ ...draft, open_until: e.target.value })}
            onBlur={() => persist({})}
          >
            <TimePickerSelect
              id={`until-period-${row.model.ID}`}
              defaultValue="24h"
              disabled
            >
              <SelectItem value="24h" text="" />
            </TimePickerSelect>
          </TimePicker>
        </div>
      )}

      <TextArea
        id={`notes-${row.model.ID}`}
        labelText="Notiz (optional)"
        rows={2}
        value={draft.notes}
        onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
        onBlur={() => persist({})}
      />
    </div>
  );
};

export default YearOpeningHoursPage;
